import { useState, useEffect, useCallback } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Stack,
    Typography,
    Button,
    CircularProgress,
    Alert,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { getExchangeAvailableTripsAPI, exchangeTicketAPI } from "../api/tickets";
import { fmtTime as fmtTimeShared } from "../utils/format";
import AppDatePicker from "./AppDatePicker";

const fmtTime = (raw) => fmtTimeShared(raw, "--:--");

function fmtDate(raw) {
    if (!raw) return "";
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw).slice(0, 10);
    return d.toLocaleDateString("sr-Latn-RS", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/**
 * Zamena polaska JEDNOSMERNE karte: izbor novog datuma → polasci ISTE linije
 * (server filtrira: slobodno mesto, ista cena, budućnost) → potvrda. Relacija,
 * tip karte i cena su nepromenjivi — prikazani su samo kao informacija.
 * Jednom zamenjena karta ne može ponovo (server odbija sa porukom).
 */
export default function ExchangeDialog({ open, onClose, ticket, onSuccess }) {
    const { t } = useTranslation();
    const [date, setDate] = useState("");
    const [trips, setTrips] = useState([]);
    const [loadingTrips, setLoadingTrips] = useState(false);
    const [tripsError, setTripsError] = useState(null);
    const [selectedTrip, setSelectedTrip] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    useEffect(() => {
        if (open) {
            setDate("");
            setTrips([]);
            setTripsError(null);
            setSelectedTrip(null);
            setSubmitError(null);
        }
    }, [open, ticket]);

    const depName = ticket?.departureStationName || ticket?.departureStation?.name || "";
    const arrName = ticket?.arrivalStationName || ticket?.arrivalStation?.name || "";
    const operatorName =
        ticket?.operatorName ||
        (typeof ticket?.operator === "string" ? ticket.operator : ticket?.operator?.name) ||
        "";
    const ticketTypeName =
        ticket?.ticketTypeName ||
        (typeof ticket?.ticketType === "string" ? ticket.ticketType : ticket?.ticketType?.name) ||
        "";
    const currentDate = fmtDate(ticket?.journeyDate || ticket?.date);
    const currentTime = fmtTime(
        ticket?.calculatedDepartureTime || ticket?.departureTime || ticket?.departureTimeStr
    );

    const fetchTrips = useCallback(async (chosenDate) => {
        if (!ticket?.id || !chosenDate) return;
        setLoadingTrips(true);
        setTripsError(null);
        setSelectedTrip(null);
        try {
            const data = await getExchangeAvailableTripsAPI(ticket.id, chosenDate);
            setTrips(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Greška pri dovlačenju polazaka za zamenu:", err);
            setTripsError(err?.response?.data?.message || t("exchange.tripsError"));
            setTrips([]);
        } finally {
            setLoadingTrips(false);
        }
    }, [ticket, t]);

    const handleDateChange = (value) => {
        setDate(value);
        if (value) fetchTrips(value);
    };

    const handleSubmit = async () => {
        if (!selectedTrip || !date) return;
        setSubmitting(true);
        setSubmitError(null);
        try {
            await exchangeTicketAPI(ticket.id, {
                tripId: selectedTrip.tripId,
                journeyDate: date,
            });
            onSuccess?.();
        } catch (err) {
            console.error("Greška pri zameni polaska:", err);
            setSubmitError(err?.response?.data?.message || t("exchange.submitError"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth scroll="paper">
            <DialogTitle sx={{ pr: 6 }}>
                {t("exchange.title")}
                <IconButton onClick={onClose} disabled={submitting} aria-label={t("common.close")} sx={{ position: "absolute", right: 8, top: 8 }}>
                    <Icon icon="mdi:close" width={22} />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {/* Postojeći detalji puta — samo informacija, menjaju se isključivo datum i polazak. */}
                <Box sx={{ bgcolor: "grey.50", border: "1px solid", borderColor: "grey.200", borderRadius: 2, p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                        {depName} → {arrName}
                    </Typography>
                    <Stack spacing={0.25}>
                        {operatorName && (
                            <Typography variant="caption" color="text.secondary">
                                {t("exchange.carrier")}: {operatorName}
                            </Typography>
                        )}
                        {ticketTypeName && (
                            <Typography variant="caption" color="text.secondary">
                                {t("exchange.ticketType")}: {ticketTypeName}
                            </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                            {t("exchange.currentDeparture")}: {currentDate}
                            {currentTime !== "--:--" ? ` · ${currentTime}` : ""}
                        </Typography>
                    </Stack>
                    <Alert severity="info" icon={<Icon icon="mdi:information-outline" width={18} />} sx={{ mt: 1.5, py: 0.25 }}>
                        {t("exchange.onlyDateNote")}
                    </Alert>
                </Box>

                <AppDatePicker
                    label={t("exchange.newDate")}
                    value={date}
                    onChange={handleDateChange}
                    disablePast
                    sx={{ mb: 2 }}
                />

                {loadingTrips && (
                    <Box sx={{ textAlign: "center", py: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                )}

                {tripsError && <Alert severity="error" sx={{ mb: 2 }}>{tripsError}</Alert>}

                {!loadingTrips && date && !tripsError && trips.length === 0 && (
                    <Alert severity="info">{t("exchange.noTrips")}</Alert>
                )}

                {trips.length > 0 && (
                    <Stack spacing={1}>
                        <Typography variant="subtitle2">{t("exchange.chooseDeparture")}</Typography>
                        {trips.map((tr) => {
                            const selected = selectedTrip?.tripId === tr.tripId;
                            return (
                                <Box
                                    key={tr.tripId}
                                    onClick={() => setSelectedTrip(tr)}
                                    sx={{
                                        border: "2px solid",
                                        borderColor: selected ? "primary.main" : "grey.200",
                                        borderRadius: 2,
                                        p: 1.5,
                                        cursor: "pointer",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        transition: "border-color 0.15s",
                                        "&:hover": { borderColor: selected ? "primary.main" : "grey.400" },
                                    }}
                                >
                                    <Stack>
                                        <Typography sx={{ fontWeight: 700 }}>
                                            {fmtTime(tr.departureTime)}
                                            {tr.arrivalTime ? ` → ${fmtTime(tr.arrivalTime)}` : ""}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {tr.lineName || ""}
                                        </Typography>
                                    </Stack>
                                    <Stack alignItems="flex-end">
                                        {typeof tr.freeSeats === "number" && (
                                            <Typography variant="caption" color="text.secondary">
                                                {t("exchange.freeSeats")}: {tr.freeSeats}
                                            </Typography>
                                        )}
                                        {selected && <Icon icon="mdi:check-circle" width={20} color="#1a1a2e" />}
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Stack>
                )}

                {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={submitting}>
                    {t("common.cancel")}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!selectedTrip || submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Icon icon="mdi:swap-horizontal" width={18} />}
                >
                    {submitting ? t("exchange.submitting") : t("exchange.confirm")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
